var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { SyncService } from "./sync.service";
const imageToURI = require('image-to-data-uri');
import { NgZone } from '@angular/core';
import { UserSettings } from "../models/user-settings";
import { CollectionService } from "./collection.service";
import { BehaviorSubject } from "rxjs";
import { PersistenceService, PersistenceException } from "./persistence.service";
import { ToastrService } from "ngx-toastr";
import { AmplifyService } from 'aws-amplify-angular';
import { Hub } from 'aws-amplify';
import { Router } from '@angular/router';
const STORAGE_KEY = 'user';
export const LOCAL_USER_ID = 'local';
export class UserService {
    constructor(collectionSvc, amplifySvc, persistSvc, syncSvc, toastr, router, ngZone) {
        this.collectionSvc = collectionSvc;
        this.amplifySvc = amplifySvc;
        this.persistSvc = persistSvc;
        this.syncSvc = syncSvc;
        this.toastr = toastr;
        this.router = router;
        this.ngZone = ngZone;
        this.user$ = new BehaviorSubject(null);
        Hub.listen('auth', ({ payload: { event, data } }) => {
            if (event === 'signIn') {
                this.ngZone.run(() => this.router.navigate(['/home/account'])).then();
            }
        });
        this.amplifySvc.authStateChange$
            .subscribe(authState => {
            if (authState.state === 'signedIn') {
                this.onCognitoUserAuth(authState.user).then();
            }
            else if (authState.state === 'signedOut') {
                this.onCognitoUserAuth(undefined).then();
            }
        });
        setTimeout(() => this.localOnlyAuthentication(), 100);
    }
    get info() {
        return this.authUser;
    }
    localOnlyAuthentication() {
        try {
            const authUserStr = localStorage.getItem(STORAGE_KEY);
            if (authUserStr) {
                this.onUserAuth(JSON.parse(authUserStr));
            }
        }
        catch (err) {
            console.log(err);
        }
    }
    renewAuthentication() {
        if (this.syncSvc.isOnline()) {
            const auth = this.amplifySvc.auth();
            auth.currentSession()
                .then(() => auth.currentAuthenticatedUser())
                .then(user => this.onCognitoUserAuth(user))
                .catch(err => {
                console.warn("renewAuthentication failure", err);
                return this.persistSvc.signOut();
            });
        }
    }
    onCognitoUserAuth(cognitoUser) {
        return __awaiter(this, void 0, void 0, function* () {
            if (cognitoUser) {
                const cognitoIdentity = yield this.amplifySvc.auth().currentUserInfo();
                console.log("cognitoIdentity", cognitoIdentity);
                const newUser = cognitoIdentity && cognitoIdentity.attributes
                    ? {
                        id: cognitoIdentity.id,
                        email: cognitoIdentity.attributes.email,
                        name: cognitoIdentity.attributes.name || "Unknown",
                        photo: cognitoIdentity.attributes.picture,
                        provider: cognitoIdentity.username.startsWith("Google") ? "Google" : "SqAC",
                    }
                    : undefined;
                return this.onUserAuth(newUser);
            }
            else {
                return this.onUserAuth(undefined);
            }
        });
    }
    createLocalAccount() {
        console.log("Creating local account");
        this.onUserAuth({
            id: LOCAL_USER_ID,
            name: "Local User",
            email: "user@localhost",
            photo: '',
            provider: "local account"
        });
    }
    onUserAuth(newUser) {
        return __awaiter(this, void 0, void 0, function* () {
            if ((this.authUser && newUser && this.authUser.id === newUser.id) || (!this.authUser && !newUser)) {
                console.log("Received user auth, but it didn't change");
                return;
            }
            this.authUser = newUser;
            if (this.authUser) {
                console.log("Login", this.authUser);
                yield this.getUserSettings();
                yield this.setPhotoFromURI(this.authUser.photo);
                if (this.settings.id === LOCAL_USER_ID && this.authUser.id !== LOCAL_USER_ID) {
                    const oldSettings = this.settings;
                    this.createNewSettings();
                    this.settings.collections = oldSettings.collections;
                    this.settings.sessions = oldSettings.sessions;
                    this.settings.activeSession = oldSettings.activeSession;
                }
                localStorage.setItem(STORAGE_KEY, JSON.stringify(this.authUser));
                if (this.settings.isDirty) {
                    yield this.localSave();
                    yield this.sync(true);
                }
                this.user$.next(this.settings);
            }
            else {
                console.log("Logout");
                localStorage.removeItem(STORAGE_KEY);
                this.settings = undefined;
                this.user$.next(null);
            }
        });
    }
    isLocalUser() {
        return this.authUser && this.authUser.id === LOCAL_USER_ID;
    }
    isCloudUser() {
        return this.authUser && this.authUser.id !== LOCAL_USER_ID;
    }
    signOut() {
        this.persistSvc.signOut().then();
        this.onUserAuth(undefined);
    }
    getUserSettings() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.authUser) {
                throw new PersistenceException(401, "not authenticated");
            }
            else if (this.settings) {
                return this.settings;
            }
            else {
                return this.persistSvc.loadUser(this.isLocalUser())
                    .then(settings => {
                    console.log("User settings loaded");
                    this.settings = settings;
                })
                    .catch((ex) => {
                    if (ex.statusCode === 404) {
                        return this.createNewSettings();
                    }
                    else {
                        throw ex;
                    }
                })
                    .then(() => {
                    return this.collectionSvc.loadFrom(this.settings);
                })
                    .then(() => {
                    let isDirty = false;
                    if (!this.settings.isCloudBacked && this.isCloudUser()) {
                        console.log('User settings are unsynced');
                        isDirty = true;
                    }
                    for (const c of Array.from(this.collectionSvc.values())) {
                        if (!c.isCloudBacked) {
                            console.log(`Collection ${c.id} is unsynced`);
                            isDirty = true;
                        }
                    }
                    if (isDirty) {
                        this.syncSvc.setDirty();
                    }
                })
                    .then(() => this.settings);
            }
        });
    }
    localSave() {
        if (this.settings && this.settings.isDirty) {
            return this.persistSvc.localStoreUser(this.settings);
        }
        else {
            return Promise.resolve(this.settings);
        }
    }
    sync(sendOnly) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isLocalUser()) {
                return this.localSave();
            }
            else if (!this.settings) {
                console.log("Not loaded - do so now?");
                if (this.authUser && !sendOnly) {
                    return this.getUserSettings();
                }
                else {
                    throw new PersistenceException(401, "Not authenticated");
                }
            }
            else if (this.settings.isDirty || !this.settings.isCloudBacked) {
                console.log("Settings have been locally changed. Saving..");
                this.settings.name = this.authUser.name || this.settings.name;
                this.settings.email = this.authUser.email || this.settings.email;
                yield this.persistSvc.cloudSaveUser(this.settings);
                yield this.toastr.success("Saved changes", "Account");
                return this.settings;
            }
            else if (!sendOnly && (yield this.persistSvc.isNewerInCloud(this.settings))) {
                console.log("Read updated content from server");
                const tmp = yield this.persistSvc.loadUser();
                if (tmp.modified.getTime() > this.settings.modified.getTime()) {
                    yield this.toastr.warning("Updated from cloud", "Account");
                    const collectionsChanged = !setsAreEqual(tmp.collections, this.settings.collections);
                    this.settings = tmp;
                    if (collectionsChanged) {
                        yield this.collectionSvc.loadFrom(this.settings);
                    }
                }
                return this.settings;
            }
            else {
                console.log("UserService.sync - No change");
                yield this.toastr.success("No updates", "Account");
                return this.settings;
            }
        });
    }
    createNewSettings() {
        console.log("Creating new UserSettings");
        const user = this.authUser;
        this.settings = new UserSettings(user.id);
        this.settings.isDirty = true;
        this.settings.isAvailable = true;
        this.settings.name = user.name;
        this.settings.email = user.email;
        ["0000/callerlab-basic", "0000/callerlab-mainstream", "0000/classics", "0000/ceder-basic"]
            .forEach(c => this.settings.collections.add(c));
        return this.settings;
    }
    setPhotoFromURI(photoUri) {
        this.authUser.photo = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAMAAABEpIrGAAAB+1BMVEWCg4aEhYiEhoiEhomFh4mFiIqGiIqGiIuGiYuHiIqHiYuHiYyIiYuIiYyIioyIio2JioyJio2Ji42JjI6KjI6KjY+LjZCLjpCMjpCMj5GNj5GNj5KNkJKOkJOPkZOPkpSPkpWQk5WRlJeSlZeTlpiTlpmTlpqVl5mVmJuVmZyXmZ2XmpyXmp2Xmp6Xm56YmpyYm56YnKCYnaCZnaCZnaGanZ+anqCanqGanqKbn6KcnqCcn6KcoKOcoKScoaSdoKSdoaWdoqaen6Geoqaeo6eepKifoaOfpKego6aho6WhpKiipqqjpaejp6qkp6qkqKukqKymqa2mqqynqauoqqyorLCsr7CusLKwtLaxs7Wxtbiztre0tri0t7m1tri1t7q3ubq3uby3u764ury5u727vsG8vsG8v8G9wMG+wMG/wcO/wsS/wsXAwsTAw8TBw8XBxcjCxMbCxcjCxsnDxcfEx8jEx8nFx8nFyMrGyMrGys3Hy83Iy87IzM7Lzc/MztDMz9HO0NLO0dLP0dLP0dTQ0tTQ09XR1NXS1dbT1dfT1djU1tjU19jU19nV19rW2NrX2dvX2tzX2t3X293Y29zY293Z3N3Z3N7a3N7a3d7a3d/b3t/b3uDc3uDc3+Dc3+Hc4OLd3+Hd4OLe4OLe4ePf4ePf4uTg4uTg4+TZCKiIAAABkElEQVQ4ja2S1VbDQBRFgxUvUIIXiru7Q2lLcXd3d3d3v4UhEILrZ5ISmmSRsHhhv53JXudOZgZDf4D9twA0vwtAHixPTixdEPCLQF0sNhRrtaoFkpZFBNiqVqrVGo1G2TEGrMETzlQsyrJ9YQMUZnPGECJ+CrA2qM1kiQHBCPiYic9gSe4WNCCqNIWjvFkoXGXHc9Q9C/+CTI9jia0zFPCE24pIjl50LhCOs0IjDIQXvApG6PaSQjgS30VO8iY82ED0schRw3VJgIE0il3mX1aPnz+DYhaJChDoy5B3D2ICOj/18f5iB0Qb6H0lKPTI35CYAIggGt3kcrnLOEUIXxSgl9WqgaciZw/nysvW2t03HfAFII/6gqw974CqSa29h3U787CRE+btYsz3LlfcCW+nQF8F8FEvw3Gv0QdgBEDbuRJHmczRLGqeVoAc9rego8ws/5DQC4A2pfbfmPbTvZ1Sh+8o3QDAkG5aYitlWUdzJmywxVf0I1oseeQ8uttwyaiNoIUmiQUHNmXFS8ZtxCdiCrz8WJjJugAAAABJRU5ErkJggg==";
        if (photoUri) {
            return new Promise((resolve) => {
                photoUri = photoUri.replace(/\?sz=50$/, '?sz=96');
                imageToURI(photoUri, (err, dataUri) => {
                    if (err) {
                        console.error("Failure loading user photo", err);
                    }
                    else {
                        this.authUser.photo = dataUri;
                    }
                    resolve();
                });
            });
        }
        else {
            return Promise.resolve();
        }
    }
}
function setsAreEqual(a, b) {
    if (a.size !== b.size) {
        return false;
    }
    let match = true;
    a.forEach(value => {
        if (!b.has(value)) {
            match = false;
        }
    });
    return match;
}
